import React from 'react';
import { signIn, signUp, signOut, confirmSignIn, resetPassword, confirmResetPassword, resendSignUpCode } from 'aws-amplify/auth';
import { trackEvent } from './utils/analytics';
import { TURNSTILE_SITE_KEY } from './constants/config';

if (!document.getElementById('remnant-fonts-auth')) {
  const link = document.createElement('link');
  link.id = 'remnant-fonts-auth';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700&family=Lora:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap';
  document.head.appendChild(link);
}

const authStyles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .auth-root {
    min-height: 100vh;
    background: #fbf8f1;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 24px;
    position: relative; overflow: hidden;
    font-family: 'Lora', Georgia, serif;
  }

  /* Background atmosphere — warm radial glow */
  .auth-bg {
    position: absolute; inset: 0; pointer-events: none;
    background:
      radial-gradient(ellipse 70% 60% at 50% 40%, rgba(15,23,42,0.04) 0%, transparent 65%),
      linear-gradient(180deg, #fbf8f1 0%, #f0ebe2 50%, #fbf8f1 100%);
  }
  .auth-grid {
    position: absolute; inset: 0; pointer-events: none;
    background-image:
      linear-gradient(rgba(15,23,42,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(15,23,42,0.03) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 60% 60% at 50% 50%, black 20%, transparent 100%);
  }

  /* Back link */
  /* F-13c: Back tucked INSIDE the card top-left — small, muted, sentence case.
     min-height 44px = strict tap-target compliance (visual size stays small via font-size). */
  .auth-back {
    position: absolute; top: 10px; left: 14px;
    background: none; border: none; cursor: pointer;
    color: #9a8d7e; font-family: 'Lora', serif;
    font-size: 0.85rem; letter-spacing: 0.2px;
    display: flex; align-items: center; gap: 5px; transition: color 0.2s;
    z-index: 10; padding: 6px 8px; min-height: 44px;
  }
  .auth-back:hover { color: #6b5f52; }

  /* Card */
  .auth-card {
    position: relative; z-index: 2;
    width: 100%; max-width: 460px;
    background: #ffffff;
    border: 1px solid rgba(15,23,42,0.12);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 40px rgba(15,23,42,0.08), 0 2px 12px rgba(15,23,42,0.04);
  }

  /* F-13c: seamless card — no top accent, no header/body cream seam or divider */
  .auth-header {
    padding: 44px 40px 22px;
    text-align: center;
  }
  /* F-13c: match the landing wordmark exactly (Lora / 1.4rem / 700 / 1px / ink) —
     values mirrored from LandingPageV2 .v2-nav-brand span (separate style scopes; can't share the class) */
  .auth-brand {
    font-family: 'Lora', Georgia, serif; font-size: 1.4rem; font-weight: 700;
    letter-spacing: 1px; color: #0f172a; margin-bottom: 22px;
  }
  .auth-tabs {
    display: flex; gap: 0;
    background: rgba(15,23,42,0.04);
    border: 1px solid rgba(15,23,42,0.12);
    border-radius: 9999px; overflow: hidden;
  }
  /* F-13c: amber active tab, fully inside the pill silhouette (no underline poking out) */
  .auth-tab {
    flex: 1; padding: 13px 10px; min-height: 46px;
    background: none; border: none; cursor: pointer;
    font-family: 'Lora', serif; font-size: 0.95rem; letter-spacing: 0.5px;
    transition: all 0.2s; color: #6b5f52;
  }
  .auth-tab:hover { color: #2a2118; }
  .auth-tab.active {
    background: linear-gradient(180deg, #AE6015 0%, #A85A0F 100%);
    color: #ffffff;
    font-weight: 600;
  }

  .auth-body { padding: 32px 40px 36px; }

  .auth-field { margin-bottom: 18px; }
  /* F-13c: labels whisper — sentence case (JSX), ~13px, muted, light tracking */
  .auth-label {
    display: block; font-family: 'Lora', serif; font-size: 13px;
    letter-spacing: 0.3px; color: #6b5f52; margin-bottom: 7px;
  }
  /* F-13c: ONE input voice — white fill always, warm ghost-border, amber focus, no fill change idle→focus */
  .auth-input {
    width: 100%; padding: 13px 16px; min-height: 48px;
    background: #ffffff;
    border: 1px solid rgba(120,70,20,0.22);
    border-radius: 10px; outline: none;
    color: #2a2118; font-family: 'Lora', serif;
    font-size: 1.05rem; transition: border-color 0.15s, box-shadow 0.15s;
  }
  .auth-input::placeholder { color: #9a8d7e; }
  .auth-input:focus {
    border-color: #A85A0F;
    box-shadow: 0 0 0 3px rgba(168,90,15,0.12);
  }

  /* F-13c: primary action closes the card — amber gradient, sentence case (JSX) */
  .auth-submit {
    width: 100%; padding: 16px; min-height: 50px;
    background: linear-gradient(180deg, #AE6015 0%, #A85A0F 100%);
    color: #fff; border: none; border-radius: 10px;
    font-family: 'Lora', serif; font-size: 1.05rem; font-weight: 600;
    letter-spacing: 0.3px; cursor: pointer;
    transition: all 0.2s; margin-top: 10px;
    box-shadow: 0 4px 18px rgba(168,90,15,0.2);
  }
  .auth-submit:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 24px rgba(168,90,15,0.32);
    background: linear-gradient(180deg, #C06A18 0%, #B0600F 100%);
  }
  .auth-submit:disabled {
    opacity: 0.5; cursor: not-allowed; transform: none;
  }

  .auth-error {
    background: rgba(197,48,48,0.06);
    border: 1px solid rgba(197,48,48,0.25);
    border-radius: 8px; padding: 14px 18px; margin-bottom: 18px;
    color: #c53030; font-size: 1rem; line-height: 1.5;
  }

  .auth-success {
    background: rgba(39,174,96,0.06);
    border: 1px solid rgba(39,174,96,0.25);
    border-radius: 8px; padding: 14px 18px; margin-bottom: 18px;
    color: #217a3e; font-size: 1rem; line-height: 1.5;
  }

  .auth-divider {
    text-align: center; margin: 20px 0 4px;
    font-family: 'Lora', serif; font-style: italic;
    font-size: 1.05rem; color: #8a7d70;
  }

  .auth-switch {
    text-align: center; margin-top: 16px;
    font-size: 1rem; color: #5a4e42; font-style: italic;
  }
  .auth-switch button {
    background: none; border: none; cursor: pointer;
    color: #3c4653; font-family: inherit; font-size: inherit;
    font-style: inherit; text-decoration: underline; transition: color 0.2s;
  }
  .auth-switch button:hover { color: #0f172a; }

  /* F-13c: resend matches the whisper voice (muted, sentence tracking, link affordance) */
  .auth-resend {
    background: none; border: none; cursor: pointer;
    color: #6b5f52; font-family: 'Lora', serif;
    font-size: 0.9rem; letter-spacing: 0.2px; text-decoration: underline;
    margin-top: 14px; display: block; width: 100%; text-align: center;
    transition: color 0.2s; padding: 8px;
  }
  .auth-resend:hover { color: #2a2118; }

  .auth-code-hint {
    font-size: 1.05rem; color: #5a4e42; font-style: italic;
    text-align: center; margin-bottom: 20px; line-height: 1.6;
  }

  .auth-beta-field {
    margin-bottom: 18px;
    padding: 18px;
    background: rgba(15,23,42,0.03);
    border: 1px solid rgba(15,23,42,0.12);
    border-radius: 10px;
  }
  .auth-beta-field .auth-label {
    color: #0f172a;
  }
  .auth-beta-hint {
    font-size: 1rem; color: #6b5e52; font-style: italic;
    margin-top: 8px; line-height: 1.5;
  }

  @media (max-width: 480px) {
    .auth-header { padding: 28px 24px 20px; }
    .auth-body { padding: 24px 24px 28px; }
    .auth-back { top: 16px; left: 16px; }
  }
`;

export default function AuthPage({ initialMode = 'signIn', onSuccess, onBack, sessionExpiredMessage }) {
  const [mode, setMode] = React.useState(initialMode); // 'signIn' | 'signUp' | 'confirm' | 'totp' | 'forgotPw' | 'resetPw'
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  // Guard: if user is already signed in, redirect to app
  React.useEffect(() => {
    import('aws-amplify/auth').then(({ getCurrentUser }) => {
      getCurrentUser().then(() => {
        // Already signed in — skip auth, go straight to app
        onSuccess();
      }).catch(() => {
        // Not signed in — this is expected, stay on auth page
      });
    });
  }, [onSuccess]);
  // Form fields
  const [email, setEmail] = React.useState(() => {
    // Pre-fill the email after an email-link confirmation that couldn't auto-sign-in
    // (e.g. opened in a different browser), so the user fills password only, not cold.
    try {
      const e = localStorage.getItem('tb_prefill_email');
      if (e) { localStorage.removeItem('tb_prefill_email'); return e; }
    } catch (err) { /* no storage */ }
    return '';
  });
  const [password, setPassword] = React.useState('');
  const [name, setName] = React.useState('');
  const [totpCode, setTotpCode] = React.useState('');
  const [pendingEmail, setPendingEmail] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [resetCode, setResetCode] = React.useState('');

  const clearMessages = () => { setError(''); setSuccess(''); };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please enter your email and password.'); return; }
    setLoading(true); clearMessages();
    try {
      const result = await signIn({ username: email.trim().toLowerCase(), password });
      if (result.isSignedIn) {
        trackEvent('login', { method: 'password' });
        onSuccess();
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setMode('totp');
        setTotpCode('');
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        setPendingEmail(email.trim().toLowerCase());
        setMode('confirm');
        setSuccess('Please check your email and tap the confirmation link.');
      }
    } catch (err) {
      if (err.name === 'UserAlreadyAuthenticatedException' || err.message?.includes('already a signed')) {
        // User is already signed in — just go to app
        onSuccess();
        return;
      } else if (err.name === 'UserNotConfirmedException') {
        setPendingEmail(email.trim().toLowerCase());
        setMode('confirm');
        setSuccess('Your account needs confirmation. Check your email and tap the confirmation link.');
      } else if (err.name === 'NotAuthorizedException') {
        setError('Incorrect email or password. Please try again.');
      } else if (err.name === 'UserNotFoundException') {
        setError('No account found with that email. Try creating one.');
      } else {
        setError(err.message || 'Sign in failed. Please try again.');
      }
    } finally { setLoading(false); }
  };

  const handleTotpConfirm = async (e) => {
    e.preventDefault();
    if (totpCode.length !== 6) { setError('Please enter the 6-digit code from your authenticator app.'); return; }
    setLoading(true); clearMessages();
    try {
      const result = await confirmSignIn({ challengeResponse: totpCode });
      if (result.isSignedIn) {
        trackEvent('login', { method: 'totp' });
        onSuccess();
      } else {
        setError('Verification failed. Please try again.');
      }
    } catch (err) {
      if (err.name === 'CodeMismatchException' || err.message?.includes('Code mismatch')) {
        setError('That code didn\'t work. Make sure you\'re entering the latest code from your authenticator app.');
      } else {
        setError(err.message || 'Verification failed. Please try again.');
      }
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email address.'); return; }
    setLoading(true); clearMessages();
    try {
      await resetPassword({ username: email.trim().toLowerCase() });
      setPendingEmail(email.trim().toLowerCase());
      setMode('resetPw');
      setSuccess('We sent a verification code to your email. Enter it below with your new password.');
    } catch (err) {
      if (err.name === 'UserNotFoundException') {
        setError('No account found with that email.');
      } else if (err.name === 'LimitExceededException') {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setError(err.message || 'Failed to send reset code. Please try again.');
      }
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetCode || !newPassword) { setError('Please enter the code and your new password.'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true); clearMessages();
    try {
      await confirmResetPassword({ username: pendingEmail, confirmationCode: resetCode.trim(), newPassword });
      setSuccess('Password reset! You can now sign in with your new password.');
      setMode('signIn');
      setPassword('');
      setResetCode('');
      setNewPassword('');
    } catch (err) {
      if (err.name === 'CodeMismatchException') {
        setError('That code didn\'t match. Check your email and try again.');
      } else if (err.name === 'ExpiredCodeException') {
        setError('That code has expired. Go back and request a new one.');
      } else {
        setError(err.message || 'Password reset failed. Please try again.');
      }
    } finally { setLoading(false); }
  };

  // ── Turnstile bot-guard (TB-115 Phase 1) ──
  const turnstileRef = React.useRef(null);
  const turnstileWidgetId = React.useRef(null);
  const [turnstileToken, setTurnstileToken] = React.useState('');
  // If the Turnstile script can't load (Cloudflare blocked by a VPN / filtering DNS, or an
  // outage), we must NOT hard-block signup — degrade gracefully and let the backend be the
  // gate. Otherwise legit users on those networks couldn't create an account at all.
  const [turnstileUnavailable, setTurnstileUnavailable] = React.useState(false);
  const [turnstileError, setTurnstileError] = React.useState('');

  // Render the Cloudflare Turnstile widget on the signup tab. Polls for the async script,
  // renders once, cleans up on leaving signup. The token is single-use.
  React.useEffect(() => {
    if (mode !== 'signUp') return undefined;
    let cancelled = false;
    setTurnstileUnavailable(false);
    setTurnstileError('');
    const render = () => {
      if (cancelled) return;
      if (window.turnstile && turnstileRef.current && turnstileWidgetId.current == null) {
        try {
          turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            callback: (t) => { setTurnstileToken(t); setTurnstileError(''); },
            'expired-callback': () => {
              setTurnstileToken('');
              if (turnstileWidgetId.current != null && window.turnstile) {
                try { window.turnstile.reset(turnstileWidgetId.current); } catch (e) {}
              }
            },
            'error-callback': () => { setTurnstileToken(''); setTurnstileError('The security check could not load. You can still continue.'); },
          });
        } catch (e) { /* already rendered / script hiccup */ }
      } else if (turnstileWidgetId.current == null) {
        setTimeout(render, 200);
      }
    };
    render();
    // If the script never loads within 5s, stop hard-gating on the token so signup still works.
    const giveUpTimer = setTimeout(() => {
      if (!cancelled && turnstileWidgetId.current == null) setTurnstileUnavailable(true);
    }, 5000);
    return () => {
      cancelled = true;
      clearTimeout(giveUpTimer);
      if (turnstileWidgetId.current != null && window.turnstile) {
        try { window.turnstile.remove(turnstileWidgetId.current); } catch (e) {}
      }
      turnstileWidgetId.current = null;
      setTurnstileToken('');
    };
  }, [mode]);

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) { setError('Please fill in all fields.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!turnstileToken && !turnstileUnavailable) { setError('Please complete the security check below, then try again.'); return; }
    setLoading(true); clearMessages();
    try {
      const result = await signUp({
        username: email.trim().toLowerCase(),
        password,
        options: {
          userAttributes: { email: email.trim().toLowerCase(), name: name.trim() },
          clientMetadata: { turnstileToken }, // TB-115: PreSignUp Lambda verifies this + auto-confirms
        }
      });
      // GA4 recommended event — the account now exists.
      trackEvent('sign_up', { method: 'email' });
      if (result.nextStep?.signUpStep === 'DONE') {
        // TB-115 Phase 2: PreSignUp auto-confirmed the user — sign them straight into the app,
        // no email gate. (Email is auto-verified so password recovery works; real "did they
        // confirm their email" is a separate, non-blocking nudge.)
        let signInResult;
        try {
          signInResult = await signIn({ username: email.trim().toLowerCase(), password });
        } catch (inner) {
          // A stale session in this browser makes signIn throw "already signed in" — clear it
          // and retry as the new account.
          const inName = inner && inner.name ? inner.name : '';
          if (inName === 'UserAlreadyAuthenticatedException' || /already.*signed in/i.test((inner && inner.message) || '')) {
            await signOut();
            signInResult = await signIn({ username: email.trim().toLowerCase(), password });
          } else { throw inner; }
        }
        if (signInResult.isSignedIn) {
          trackEvent('login', { method: 'post_signup' });
          trackEvent('first_login', { method: 'email' });
          // handleAuthSuccess retries getCurrentUser, which tolerates the brief window where the
          // session isn't readable yet right after a fresh signUp→signIn (incl. an account switch).
          onSuccess();
        } else {
          setError('Your account was created. Please sign in.');
          setMode('signIn');
        }
      } else if (result.nextStep?.signUpStep === 'CONFIRM_SIGN_UP') {
        // Backward-compatible fallback (if auto-confirm is ever rolled back).
        setPendingEmail(email.trim().toLowerCase());
        setMode('confirm');
        setSuccess(`We sent a confirmation link to ${email}. Check your inbox, and your spam folder if you don't see it.`);
      } else {
        onSuccess();
      }
    } catch (err) {
      // Turnstile tokens are single-use — reset the widget so a retry gets a fresh one.
      if (window.turnstile && turnstileWidgetId.current != null) {
        try { window.turnstile.reset(turnstileWidgetId.current); } catch (e) {}
      }
      setTurnstileToken('');
      if (err.name === 'UsernameExistsException') {
        setError('An account with that email already exists. Try signing in.');
      } else if (err.name === 'InvalidPasswordException') {
        setError('Password must be at least 8 characters and include a number.');
      } else {
        setError(err.message || 'Sign up failed. Please try again.');
      }
    } finally { setLoading(false); }
  };

  // NOTE: email confirmation is link-only (TB-110). The old handleConfirm
  // (6-digit code entry) was removed; confirmation + first login now happen on
  // ConfirmPage (/confirm), which fires the login / first_login funnel events.

  const handleResend = async () => {
    setLoading(true); clearMessages();
    try {
      await resendSignUpCode({ username: pendingEmail });
      setSuccess('A new confirmation link has been sent to your email.');
    } catch (err) {
      setError(err.message || 'Could not resend the email. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="auth-root">
      <style>{authStyles}</style>
      <div className="auth-bg" />
      <div className="auth-grid" />

      <div className="auth-card">
        {onBack && (
          <button className="auth-back" onClick={onBack}>
            ← Back
          </button>
        )}
        <div className="auth-header">
          <div className="auth-brand">Talebound</div>
          {mode !== 'confirm' && mode !== 'totp' && mode !== 'forgotPw' && mode !== 'resetPw' && (
            <div className="auth-tabs">
              <button className={`auth-tab${mode === 'signIn' ? ' active' : ''}`} onClick={() => { setMode('signIn'); clearMessages(); }}>
                Sign In
              </button>
              <button className={`auth-tab${mode === 'signUp' ? ' active' : ''}`} onClick={() => { setMode('signUp'); clearMessages(); }}>
                Create Account
              </button>
            </div>
          )}
        </div>

        <div className="auth-body">
          {sessionExpiredMessage && (
            <div style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontFamily: "'Lora', Georgia, serif", color: '#4e3f2c', fontSize: '0.98rem', lineHeight: 1.5, textAlign: 'center' }}>
              {sessionExpiredMessage}
            </div>
          )}
          {error && <div className="auth-error">{error}</div>}
          {success && <div className="auth-success">{success}</div>}

          {/* ── SIGN IN ── */}
          {mode === 'signIn' && (
            <form onSubmit={handleSignIn}>
              <div className="auth-field">
                <label className="auth-label">Email</label>
                <input className="auth-input" type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" autoFocus />
              </div>
              <div className="auth-field">
                <label className="auth-label">Password</label>
                <input className="auth-input" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
              </div>
              <button className="auth-submit" type="submit" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
              <div style={{ textAlign: 'center', marginTop: '12px', marginBottom: '8px' }}>
                <button type="button" onClick={() => { setMode('forgotPw'); clearMessages(); }}
                  style={{ background: 'none', border: 'none', color: '#3c4653', cursor: 'pointer', fontFamily: "'Lora', serif", fontSize: '1rem', fontStyle: 'italic', textDecoration: 'underline', padding: '6px' }}>
                  Forgot your password?
                </button>
              </div>
              <div className="auth-switch">
                Don't have an account?{' '}
                <button type="button" onClick={() => { setMode('signUp'); clearMessages(); }}>Create one free</button>
              </div>
            </form>
          )}

          {/* ── SIGN UP ── */}
          {mode === 'signUp' && (
            <form onSubmit={handleSignUp}>
              <div className="auth-field">
                <label className="auth-label">Your name</label>
                <input className="auth-input" type="text" placeholder="First and last name" value={name} onChange={e => setName(e.target.value)} autoComplete="name" autoFocus />
              </div>
              <div className="auth-field">
                <label className="auth-label">Email</label>
                <input className="auth-input" type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
              </div>
              <div className="auth-field">
                <label className="auth-label">Password</label>
                <input className="auth-input" type="password" placeholder="At least 8 characters" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" />
              </div>
              <div ref={turnstileRef} style={{ display: 'flex', justifyContent: 'center', marginBottom: (turnstileError || turnstileUnavailable) ? 6 : 14 }} />
              {(turnstileError || turnstileUnavailable) && (
                <p style={{ textAlign: 'center', fontSize: 13, color: '#8a6d3b', marginTop: 0, marginBottom: 14 }}>
                  {turnstileError || 'Security verification is temporarily unavailable. You can still create your account.'}
                </p>
              )}
              <button className="auth-submit" type="submit" disabled={loading || (!turnstileToken && !turnstileUnavailable)}>
                {loading ? 'Creating account...' : 'Create my account'}
              </button>
              <div className="auth-switch">
                Already have an account?{' '}
                <button type="button" onClick={() => { setMode('signIn'); clearMessages(); }}>Sign in here</button>
              </div>
            </form>
          )}

          {/* ── CONFIRM EMAIL ── */}
          {mode === 'confirm' && (
            <div>
              <p className="auth-code-hint">
                Almost there! We emailed a confirmation link to <strong style={{ color: '#0f172a' }}>{pendingEmail}</strong>.
                Tap it to finish setting up your account, then you're in.
              </p>
              <button type="button" className="auth-resend" onClick={handleResend} disabled={loading}>
                Resend email
              </button>
            </div>
          )}

          {/* ── TOTP / TWO-FACTOR ── */}
          {mode === 'totp' && (
            <form onSubmit={handleTotpConfirm}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ fontSize: '2.4rem', marginBottom: '12px' }}></div>
                <p style={{ color: '#5a4e42', fontSize: '1.05rem', lineHeight: 1.7, fontFamily: "'Lora', serif" }}>
                  Open your authenticator app and enter the 6-digit code shown there.
                </p>
              </div>
              <div className="auth-field">
                <label className="auth-label">Authenticator code</label>
                <input className="auth-input" type="text" placeholder="000000" value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoFocus inputMode="numeric" maxLength={6}
                  style={{ letterSpacing: '8px', fontSize: '1.5rem', textAlign: 'center', fontFamily: 'monospace' }} />
              </div>
              <p style={{ color: '#6b5e52', fontSize: '1rem', fontStyle: 'italic', textAlign: 'center', marginBottom: '16px', lineHeight: 1.6 }}>
                The code changes every 30 seconds. Enter the current one.
              </p>
              <button className="auth-submit" type="submit" disabled={loading || totpCode.length !== 6}>
                {loading ? 'Verifying...' : 'Verify & sign in'}
              </button>
              <div className="auth-switch">
                <button type="button" onClick={() => { setMode('signIn'); clearMessages(); setTotpCode(''); }}>← Back to sign in</button>
              </div>
            </form>
          )}

          {/* ── FORGOT PASSWORD ── */}
          {mode === 'forgotPw' && (
            <form onSubmit={handleForgotPassword}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ fontSize: '2.4rem', marginBottom: '12px' }}>🔑</div>
                <p style={{ color: '#5a4e42', fontSize: '1.05rem', lineHeight: 1.7, fontFamily: "'Lora', serif" }}>
                  Enter your email address and we'll send you a code to reset your password.
                </p>
              </div>
              <div className="auth-field">
                <label className="auth-label">Email</label>
                <input className="auth-input" type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} autoFocus autoComplete="email" />
              </div>
              <button className="auth-submit" type="submit" disabled={loading}>
                {loading ? 'Sending code...' : 'Send reset code'}
              </button>
              <div className="auth-switch">
                <button type="button" onClick={() => { setMode('signIn'); clearMessages(); }}>← Back to sign in</button>
              </div>
            </form>
          )}

          {/* ── RESET PASSWORD ── */}
          {mode === 'resetPw' && (
            <form onSubmit={handleResetPassword}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ fontSize: '2.4rem', marginBottom: '12px' }}>✉️</div>
                <p style={{ color: '#5a4e42', fontSize: '1.05rem', lineHeight: 1.7, fontFamily: "'Lora', serif" }}>
                  We sent a code to <strong style={{ color: '#0f172a' }}>{pendingEmail}</strong>. Enter it below with your new password.
                </p>
              </div>
              <div className="auth-field">
                <label className="auth-label">Reset code</label>
                <input className="auth-input" type="text" placeholder="123456" value={resetCode} onChange={e => setResetCode(e.target.value)} autoFocus inputMode="numeric" maxLength={6} style={{ letterSpacing: '6px', fontSize: '1.5rem', textAlign: 'center' }} />
              </div>
              <div className="auth-field">
                <label className="auth-label">New password</label>
                <input className="auth-input" type="password" placeholder="At least 8 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} autoComplete="new-password" />
              </div>
              <button className="auth-submit" type="submit" disabled={loading}>
                {loading ? 'Resetting...' : 'Reset password'}
              </button>
              <div className="auth-switch">
                <button type="button" onClick={() => { setMode('forgotPw'); clearMessages(); setResetCode(''); setNewPassword(''); }}>← Didn't get the code? Try again</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}